import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  DollarSign, Users, Copy, Check, AlertCircle, Loader2, ExternalLink,
  Sparkles, TrendingUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";

// Phase 25.11 — In-app dashboard for the analyst's own view of their
// earnings, referrals, and share URL.

export default function AnalystDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const reload = () => {
    api.get("/analysts/me")
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => {
        setErr(e?.response?.data?.detail || "Couldn't load your analyst profile.");
        setLoading(false);
      });
  };
  useEffect(() => { reload(); }, []);

  const toggleLeaderboardVisible = async (next) => {
    setSavingPrefs(true);
    try {
      await api.post("/analysts/me/prefs", { leaderboard_visible: next });
      toast.success(next ? "You're visible on the public leaderboard." : "Hidden from leaderboard.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save preference.");
    } finally {
      setSavingPrefs(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the URL to copy manually");
    }
  };

  if (loading) {
    return (
      <div className="p-6 font-mono text-xs text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
        Loading your analyst dashboard…
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-4 sm:p-6 space-y-5" data-testid="analyst-dashboard-error">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · ANALYST</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase mt-1">Analyst Dashboard</h1>
        </div>
        <Panel title="// NO ANALYST ACCOUNT">
          <div className="p-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#FFB000] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-zinc-300 leading-relaxed">{err}</p>
              <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
                The DARA Expert programme pays you 20% of MRR for the first 12 months of every
                customer you refer, plus 60% of every paid session you deliver (Phase 2 — coming).
                Apply once, get a referral code, start earning.
              </p>
              <Link
                to="/experts/apply"
                target="_blank"
                data-testid="analyst-apply-link"
                className="mt-5 inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:opacity-90"
              >
                APPLY TO JOIN
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  const a = data.analyst;
  const isApproved = a.status === "approved";
  const isPending = a.status === "pending";
  const isRejected = a.status === "rejected";

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6" data-testid="analyst-dashboard">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · ANALYST</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase mt-1">Analyst Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl leading-relaxed">
          Your referrals, your earnings, your share link. Refer DARA to data-driven businesses,
          earn 20% of their MRR for 12 months.
        </p>
      </div>

      {/* Status banner */}
      {isPending && (
        <Panel title="// APPLICATION STATUS" testId="analyst-status-pending">
          <div className="p-5 flex items-start gap-3">
            <Loader2 className="w-5 h-5 text-[#FFB000] flex-shrink-0 animate-spin" />
            <div>
              <div className="font-heading font-bold text-base">Under review</div>
              <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
                We review every application personally. Expect a response within 3 business days
                of submission. Your referral code activates the moment you're approved.
              </p>
            </div>
          </div>
        </Panel>
      )}
      {isRejected && (
        <Panel title="// APPLICATION STATUS" testId="analyst-status-rejected">
          <div className="p-5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#FF6B47] flex-shrink-0" />
            <div>
              <div className="font-heading font-bold text-base">Application not approved</div>
              <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
                We weren't able to approve this application. If you'd like to discuss or re-apply
                with more detail, email <a href="mailto:hello@daraos.ai" className="text-[#FFB000] hover:underline">hello@daraos.ai</a>.
              </p>
            </div>
          </div>
        </Panel>
      )}

      {isApproved && (
        <>
          {/* Earnings tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Tile
              icon={DollarSign}
              label="LIFETIME EARNED"
              value={`$${(a.earnings_lifetime_usd || 0).toFixed(2)}`}
              sub="Across all referrals"
              testid="tile-lifetime"
            />
            <Tile
              icon={TrendingUp}
              label="UNPAID BALANCE"
              value={`$${(a.earnings_unpaid_usd || 0).toFixed(2)}`}
              sub="Pending next payout"
              testid="tile-unpaid"
              accent="#FFB000"
            />
            <Tile
              icon={Users}
              label="REFERRALS"
              value={`${a.referrals_count || 0}`}
              sub={`${a.referrals_active || 0} active this month`}
              testid="tile-referrals"
            />
          </div>

          {/* Share link */}
          <Panel title="// YOUR REFERRAL LINK" testId="analyst-share-panel">
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">CODE</span>
                <span className="font-mono text-base text-[#FFB000] font-bold">{a.code}</span>
                <span className="px-2 py-0.5 border border-zinc-800 bg-zinc-950 text-zinc-400 font-mono text-[9px] uppercase tracking-widest">
                  {a.tier?.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center gap-2 border border-zinc-800 bg-zinc-950 p-3">
                <span className="font-mono text-xs text-zinc-300 truncate flex-1" data-testid="analyst-share-url">
                  {data.share_url}
                </span>
                <button
                  onClick={() => copy(data.share_url)}
                  data-testid="analyst-copy-btn"
                  className="flex-shrink-0 border border-zinc-700 hover:border-[#FFB000] hover:text-[#FFB000] px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? "COPIED" : "COPY"}
                </button>
              </div>
              <div className="font-mono text-[10px] text-zinc-500 leading-relaxed">
                Share this link in posts, emails, or direct messages. When someone signs up via your
                link and pays, you earn {Math.round((data.policy.referral_pct || 0.20) * 100)}% of their MRR
                for the first {data.policy.referral_duration_months || 12} months.
              </div>
            </div>
          </Panel>

          {/* Recent referrals */}
          <Panel title="// RECENT REFERRALS" testId="analyst-referrals-panel">
            <div className="p-5">
              {(!data.referrals || data.referrals.length === 0) ? (
                <div className="font-mono text-xs text-zinc-500 italic">
                  No referrals yet. Share your link above to start earning.
                </div>
              ) : (
                <div className="divide-y divide-zinc-900">
                  {data.referrals.map((r) => (
                    <div key={r.id} className="py-3 flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[11px] uppercase tracking-widest text-white">
                          {r.workspace_id?.slice(0, 8)}…
                        </div>
                        <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                          {new Date(r.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 border font-mono text-[9px] uppercase tracking-widest ${
                        r.status === "active"
                          ? "border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]"
                          : "border-zinc-800 bg-zinc-950 text-zinc-400"
                      }`}>
                        {r.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Panel>

          {/* Payout note */}
          <Panel title="// PAYOUTS · STATUS">
            <div className="p-5 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-[#FFB000] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-bold text-base">Earnings accrue today, paid via Stripe Connect once live</div>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
                  DARA's Stripe live-keys integration is being finalised. Until then, your earnings
                  accrue in your <strong className="text-white">unpaid balance</strong> tile above.
                  When Stripe Connect is enabled, you'll receive an email to connect your bank account —
                  the entire unpaid balance pays out automatically on the first of the following month.
                </p>
              </div>
            </div>
          </Panel>

          {/* Privacy preferences */}
          <Panel title="// PRIVACY · LEADERBOARD VISIBILITY" testId="analyst-prefs-panel">
            <div className="p-5 flex items-start gap-4 flex-wrap sm:flex-nowrap">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-base">Show on public Top-10 leaderboard</div>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
                  When you're in the top 10 earners, your <strong className="text-white">first name + country + lifetime earnings</strong> appear on the public daraos.ai/experts page.
                  No email, no full name, no code — ever. Toggle this off if you prefer privacy.
                </p>
              </div>
              <button
                type="button"
                onClick={() => toggleLeaderboardVisible(!a.leaderboard_visible)}
                disabled={savingPrefs}
                data-testid="analyst-prefs-leaderboard-toggle"
                role="switch"
                aria-checked={a.leaderboard_visible !== false}
                className={`relative inline-flex h-7 w-12 items-center transition-colors flex-shrink-0 disabled:opacity-50 ${
                  a.leaderboard_visible !== false ? "bg-[#FFB000]" : "bg-zinc-800"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform bg-black transition-transform ${
                    a.leaderboard_visible !== false ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, testid, accent = "#FFFFFF" }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950/40 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      </div>
      <div className="font-heading font-black text-3xl tracking-tight" style={{ color: accent }}>
        {value}
      </div>
      <div className="font-mono text-[10px] text-zinc-600 mt-1">{sub}</div>
    </div>
  );
}
